import dagre from 'dagre';
import { graph } from './designTokens';

/**
 * Layouts nodes using Dagre algorithm with custom Y-axis positioning
 * for agent graph topology visualization.
 *
 * @param {Array} nodes - ReactFlow nodes array
 * @param {Array} edges - ReactFlow edges array
 * @param {string} direction - Layout direction ('TB', 'LR', 'BT', 'RL')
 * @returns {Array} - Nodes with updated positions
 */
export const layoutNodes = (nodes, edges, direction = 'LR') => {
  if (!nodes || nodes.length === 0) return nodes;
  if (!Array.isArray(nodes)) throw new Error('layoutNodes: nodes must be an array');
  if (!Array.isArray(edges)) throw new Error('layoutNodes: edges must be an array');

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Node dimensions from design system
  const nodeWidth = graph.nodes.dimensions.width;
  const nodeHeight = graph.nodes.dimensions.height;

  // Configure graph layout
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: graph.layout.dagre.nodesep,
    ranksep: graph.layout.dagre.ranksep,
    marginx: graph.layout.dagre.margin,
    marginy: graph.layout.dagre.margin
  });

  // Add nodes to dagre graph
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Run dagre layout algorithm
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);

    return {
      ...node,
      position: {
        // Center the node on the calculated position
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2
      }
    };
  });

  return layoutedNodes;
};

/**
 * Layouts nodes using custom hierarchical and temporal layout.
 * Arranges nodes similar to timeline view: hierarchical with temporal ordering.
 * Siblings are positioned horizontally after all descendants of previous siblings.
 *
 * @param {Array} nodes - ReactFlow nodes array (spans)
 * @param {Array} edges - ReactFlow edges array
 * @returns {Array} - Nodes with updated positions
 */
export const layoutNodesCustom = (nodes, edges) => {
  if (!Array.isArray(nodes)) throw new Error('layoutNodesCustom requires nodes array');
  if (!Array.isArray(edges)) throw new Error('layoutNodesCustom requires edges array');

  if (nodes.length === 0) return nodes;

  // Build parent-child relationships from edges
  const childrenMap = new Map(); // parentId -> [childIds]
  const parentMap = new Map(); // childId -> parentId

  edges.forEach(edge => {
    if (!childrenMap.has(edge.source)) {
      childrenMap.set(edge.source, []);
    }
    childrenMap.get(edge.source).push(edge.target);
    parentMap.set(edge.target, edge.source);
  });

  // Find root node (node with no parent)
  const rootNode = nodes.find(n => !parentMap.has(n.id));
  if (!rootNode) {
    // Fallback to first node if no clear root
    return layoutNodes(nodes, edges, 'LR');
  }

  // Sort children by start_time for each parent
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  childrenMap.forEach((children, parentId) => {
    children.sort((aId, bId) => {
      const aNode = nodeMap.get(aId);
      const bNode = nodeMap.get(bId);
      const aStartTime = aNode?.data?.start_time || aNode?.data?.startTime || 0;
      const bStartTime = bNode?.data?.start_time || bNode?.data?.startTime || 0;
      return new Date(aStartTime) - new Date(bStartTime);
    });
  });

  // Layout using DFS to ensure siblings are placed after all descendants
  const nodeWidth = graph.nodes.dimensions.width;
  const nodeHeight = graph.nodes.dimensions.height;
  const horizontalSpacing = graph.layout.custom.nodesep;
  const verticalSpacing = graph.layout.custom.ranksep;

  const positions = new Map();
  let nextXByDepth = new Map(); // Track next available X position for each depth

  function layoutSubtree(nodeId, depth, startX) {
    const node = nodeMap.get(nodeId);
    if (!node) return startX;

    // Layout children first to determine their span
    const children = childrenMap.get(nodeId) || [];
    let currentX = startX;
    const childPositions = [];

    children.forEach(childId => {
      // Get the next available X at the child's depth
      const childDepth = depth + 1;
      const nextX = nextXByDepth.get(childDepth) || 0;

      // Use the maximum of currentX and nextX to ensure no overlap
      const childStartX = Math.max(currentX, nextX);

      // Layout child subtree and get the rightmost X position used
      const subtreeEndX = layoutSubtree(childId, childDepth, childStartX);

      // Track this child's position
      childPositions.push(positions.get(childId).x);

      // Next sibling should start after this subtree
      currentX = subtreeEndX;
    });

    // Position current node centered above its children
    let x;
    if (childPositions.length > 0) {
      // Center parent above children
      const leftmostChild = Math.min(...childPositions);
      const rightmostChild = Math.max(...childPositions);
      x = leftmostChild + (rightmostChild - leftmostChild) / 2;
    } else {
      // No children, use startX
      x = startX;
    }

    const y = depth * (nodeHeight + verticalSpacing);
    positions.set(nodeId, { x, y });
    nextXByDepth.set(depth, Math.max(x + nodeWidth + horizontalSpacing, currentX));

    // Return the rightmost X position used by this subtree
    return currentX > startX ? currentX : (startX + nodeWidth + horizontalSpacing);
  }

  // Start layout from root at position (0, 0)
  layoutSubtree(rootNode.id, 0, 0);

  // Apply positions to nodes
  const positionedNodes = nodes.map(node => {
    const position = positions.get(node.id);
    if (!position) {
      console.warn(`No position found for node ${node.id}`);
      return { ...node, position: { x: 0, y: 0 } };
    }
    return { ...node, position };
  });

  return positionedNodes;
};
